import React, { useState, useEffect } from 'react';

import { Loader, useMantineTheme, MultiSelect, MultiSelectProps, SelectItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompletePackage from 'hooks/package/useAutoCompletePackage';
import { getFilteredMultiSelectPrevState } from 'utils/functions';

const PackageMultiSelect = ({
  icon,
  includeArchived = false,
  ...multiSelectProps
}: Omit<MultiSelectProps, 'data'> & {
  onChange: (value: string[]) => void;
  includeArchived?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();

  const [autocompleteData, setAutocompleteData] = useState<SelectItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 250);

  const { data, isSuccess, isLoading } = useAutoCompletePackage({
    search: debouncedSearchTerm,
    includeArchived,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const resultsFormattedData = data.data.results.map(packageInstance => ({
        value: packageInstance.packageId,
        label: packageInstance.name,
      }));

      const filteredPrevData = (prevState: SelectItem[]): SelectItem[] =>
        getFilteredMultiSelectPrevState(prevState, resultsFormattedData, multiSelectProps.value || []);

      setAutocompleteData(prevState => [...filteredPrevData(prevState), ...resultsFormattedData]);
    }
  }, [isSuccess, data, multiSelectProps.value]);

  return (
    <MultiSelect
      onSearchChange={setSearchTerm}
      data={autocompleteData}
      nothingFound={isLoading ? 'Packages are loading' : 'Packages are not found'}
      rightSection={isLoading && <Loader size={theme.fontSizes.md} />}
      searchable
      {...multiSelectProps}
    />
  );
};

export default PackageMultiSelect;
